import { forwardRef, useCallback } from 'react';
import { colorsPalette, styled, useUiEvent } from '@lingoda/ui';
import type { RouterLinkProps, TabValue } from '@lingoda/router';
import { RouterLink } from '@lingoda/router';
import { actionClick } from '@lingoda/analytics';
import { BottomNavigationAction } from './BottomNavigation';
import type { BottomNavigationActionProps } from './BottomNavigation';

interface Props extends BottomNavigationActionProps {
    value: TabValue;
    icon: React.ReactNode;
    track?: string;
}

export type ButtonProps = Props & Omit<RouterLinkProps, 'innerRef'>;

export const BottomNavigationLinkAction = ({
    value,
    icon,
    label,
    track,
    onClick,
    ...props
}: ButtonProps) => {
    const addEvent = useUiEvent();
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (track) {
                void addEvent(actionClick(track));
            }
            if (onClick) {
                onClick(e);
            }
        },
        [track, addEvent, onClick],
    );

    return (
        <BottomNavigationActionStyled
            onClick={handleClick}
            value={value}
            label={label}
            icon={icon}
            component={NavigationLink}
            {...props}
        />
    );
};

const NavigationLink = forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'innerRef'>>(
    (props, ref) => <RouterLinkStyled innerRef={ref} {...props} />,
);
NavigationLink.displayName = 'NavigationLink';

const labelStyle = {
    lineHeight: '0.875rem',
    fontSize: '0.75rem',
    fontWeight: '600',
};
const BottomNavigationActionStyled = styled(BottomNavigationAction)(({ theme }) => ({
    padding: theme.spacing(1),
    width: theme.spacing(9),
    color: colorsPalette.blackAlpha[30],

    '.MuiBottomNavigationAction-label': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...labelStyle,
    },

    '&& .Mui-selected': labelStyle,
}));

const RouterLinkStyled = styled(RouterLink)(() => ({
    textAlign: 'center',
    ...labelStyle,
}));
